import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import * as React from 'react';
import { is_supported_mobile_browser } from 'js/browser/browser_detection';
import { useRootPreviewMetadata } from 'js/file_viewer/api/data';
import { useNavigateToPageCallback } from 'js/file_viewer/api/navigation';
import { useActiveEditorId, useActiveTextHighlighterId } from 'js/file_viewer/api/ui';
import * as css from 'js/file_viewer/components/preview.module.css';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { useSetLocalErrorCallback } from 'js/file_viewer/core/atoms/error';
import { rootFileInfoAtom, rootFileMetadataAtom, sharedLinkInfoAtom, } from 'js/file_viewer/core/atoms/metadata';
import { useFileViewerAtom, useSetFileViewerAtom } from 'js/file_viewer/core/atoms/use_atom';
import { getPreviewKeyFromFileInfo } from 'js/file_viewer/core/data/preview_key';
import { EventType, UserActionContext } from 'js/file_viewer/core/logging/constants';
import { useCurrentPreviewMetadata } from 'js/file_viewer/core/queries/preview_metadata';
import { maybeCopyPreviewMetadataIntoQueryClient } from 'js/file_viewer/core/queries/utils/preview_metadata';
import { makeApiLoggerTags } from 'js/file_viewer/core/riviera/api_logger';
import { ErrorLifecycle, FVError, FVErrorCode } from 'js/file_viewer/core/types/errors';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { getWillCloudDocPreviewAsSsrDoc } from 'js/file_viewer/core/utils/cloud_docs';
import { DEFAULT_FILE_CONFIG } from 'js/file_viewer/core/utils/config';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
import { useContentRect } from 'js/file_viewer/hooks/content_rect';
import { useEndSession, useInitializePreviewSession, useSupportConfirmed, } from 'js/file_viewer/hooks/lifecycle_logging';
import { useLogPreviewEvent, usePurgeQueuedEvents } from 'js/file_viewer/hooks/logging';
import { useCurrentPreviewKey } from 'js/file_viewer/hooks/selectors';
import { useDelayCallback } from 'js/file_viewer/hooks/useDelayCallback';
import { PreviewKeyboardBindings } from 'js/file_viewer/keyboard/preview_keyboard_bindings';
import { LoadingIndicator } from 'js/file_viewer/loading_indicator/loading_indicator';
import { PreviewsTimingEvents, useMarkTimingEventOnMount } from 'js/file_viewer/logging/performance';
import { useTimingLogging } from 'js/file_viewer/logging/timing';
import { parseOverlayEditor } from 'js/file_viewer/plugins/types';
import { useFileViewerPrefetch } from 'js/file_viewer/prefetch/frontend/frontend_prefetch';
import { AsyncPreviewArchiveWithRivieraFetch as AsyncPreviewArchive } from 'js/file_viewer/preview_archive/async_preview_archive';
import { useMaybeSetPasswordProtectedArchiveError } from 'js/file_viewer/preview_archive/utils';
import { AsyncPreviewAudioVideoWithLifecycleLogging as AsyncPreviewAudioVideo } from 'js/file_viewer/preview_audio_video/async_preview_video';
import { AsyncPreviewCloudDocWithRivieraFetch as AsyncPreviewCloudDoc } from 'js/file_viewer/preview_cloud_doc/async_preview_cloud_doc';
import { usePreviewContent } from 'js/file_viewer/preview_content/preview_content_sub_region';
import { AsyncPreviewDocWithRiviera as AsyncPreviewDoc } from 'js/file_viewer/preview_doc/async_preview_doc';
import { AsyncPreviewError } from 'js/file_viewer/preview_error/async_preview_error';
import { ErrorBoundary } from 'js/file_viewer/preview_error/error_boundary';
import { AsyncPreviewHTMLWithRivieraFetch as AsyncPreviewHTML } from 'js/file_viewer/preview_html/async_preview_html';
import { AsyncPreviewImageWithRivieraFetch as AsyncPreviewImage } from 'js/file_viewer/preview_image/async_preview_image';
import { AsyncPreviewLinkfileWithLifecycleLogging as AsyncPreviewLinkfile } from 'js/file_viewer/preview_linkfile/async_preview_linkfile';
import { AsyncPreviewTextWithRivieraFetch as AsyncPreviewText } from 'js/file_viewer/preview_text/async_preview_text';
import { useMaybeOpenEditorBasedOnUrl } from 'js/file_viewer/utils/auto_open';
export const Preview = (props) => {
    const { featureConfig, sourceSession, isLoggedIn, logger, apiV2ClientShim } = useFileViewerContext();
    const previewMetadata = useRootPreviewMetadata();
    const queryClient = useQueryClient();
    useMarkTimingEventOnMount(PreviewsTimingEvents.PREVIEW_COMPONENT_MOUNT);
    const apiLogger = React.useMemo(() => {
        var _a;
        return ({
            logDuration: () => Promise.resolve(logger === null || logger === void 0 ? void 0 : logger.logDuration),
            tags: makeApiLoggerTags(sourceSession.source_context, isLoggedIn, (_a = previewMetadata.content) === null || _a === void 0 ? void 0 : _a['.tag']),
        });
    }, [isLoggedIn, logger === null || logger === void 0 ? void 0 : logger.logDuration, previewMetadata.content, sourceSession.source_context]);
    useFileViewerPrefetch(queryClient, previewMetadata, props.fileInfo, apiV2ClientShim, featureConfig.videojs_preload, is_supported_mobile_browser(), props.sharedLinkInfo, apiLogger);
    return (React.createElement(PreviewContentRectSizer, null, ({ width, height }) => React.createElement(PreviewWithSize, { ...props, width: width, height: height })));
};
Preview.displayName = "Preview"
// Exported for mocking in mock_size.tsx only.
export const PreviewContentRectSizer = (props) => {
    const { children } = props;
    const { nodeRef, contentRect } = useContentRect();
    return (React.createElement("div", { className: cx(css.container, css.containerV2), ref: nodeRef, id: "fvsdk-container" }, contentRect && contentRect && (children === null || children === void 0 ? void 0 : children(contentRect))));
};
PreviewContentRectSizer.displayName = "PreviewContentRectSizer"
const PreviewWithSize = (props) => {
    var _a, _b, _c, _d, _e, _f;
    const { featureConfig, sourceSession } = useFileViewerContext();
    const setSharedLinkInfoAtom = useSetFileViewerAtom(sharedLinkInfoAtom);
    const PreviewContent = usePreviewContent();
    React.useMemo(() => {
        var _a;
        if (props.fileInfo && props.prewarmedPreviewMetadata && ((_a = props.fileMetadata) === null || _a === void 0 ? void 0 : _a.size)) {
            maybeCopyPreviewMetadataIntoQueryClient(props.prewarmedPreviewMetadata, props.fileInfo, props.fileMetadata.size, props.sharedLinkInfo);
        }
    }, [
        props.fileInfo,
        props.prewarmedPreviewMetadata,
        (_a = props.fileMetadata) === null || _a === void 0 ? void 0 : _a.size,
        props.sharedLinkInfo,
    ]);
    const [fileInfo, setRootFileInfo] = useFileViewerAtom(rootFileInfoAtom);
    const [rootMetadata, setRootFileMetadata] = useFileViewerAtom(rootFileMetadataAtom);
    // If the caller didn't specify file info, then it will eventually be populated in state by the query that fetches
    // previewMetadata. In that case, `useRootFile` will re-render.
    const fileInfoFromPropsOrRpc = (_b = props.fileInfo) !== null && _b !== void 0 ? _b : fileInfo;
    const fileMetadataFromPropsOrRpc = (_c = props.fileMetadata) !== null && _c !== void 0 ? _c : rootMetadata;
    const rootPreviewKey = React.useMemo(() => (fileInfoFromPropsOrRpc ? getPreviewKeyFromFileInfo(fileInfoFromPropsOrRpc) : undefined), [fileInfoFromPropsOrRpc]);
    React.useEffect(() => {
        var _a;
        if (((_a = props.fileInfo) === null || _a === void 0 ? void 0 : _a.file_id) && rootPreviewKey) {
            setRootFileInfo({ ...props.fileInfo, previewKey: rootPreviewKey });
        }
    }, [props.fileInfo, rootPreviewKey, setRootFileInfo]);
    React.useEffect(() => {
        setRootFileMetadata(props.fileMetadata);
    }, [props.fileMetadata, setRootFileMetadata]);
    React.useEffect(() => {
        if (props.sharedLinkInfo) {
            setSharedLinkInfoAtom(props.sharedLinkInfo);
        }
    }, [setSharedLinkInfoAtom, props.sharedLinkInfo]);
    React.useEffect(() => {
        var _a;
        if (fileInfo &&
            props.fileInfo &&
            (fileInfo.file_id !== ((_a = props.fileInfo) === null || _a === void 0 ? void 0 : _a.file_id) || fileInfo.url !== props.fileInfo.url)) {
            sourceSession.was_flipped = true;
        }
    });
    const fileConfig = React.useMemo(() => ({ ...DEFAULT_FILE_CONFIG, ...props.fileConfig }), [props.fileConfig]);
    const activeEditorId = useActiveEditorId();
    const activeOverlayEditor = activeEditorId
        ? parseOverlayEditor((_d = props.editors) === null || _d === void 0 ? void 0 : _d[activeEditorId])
        : undefined;
    useMaybeOpenEditorBasedOnUrl();
    const previewMetadata = useRootPreviewMetadata();
    return (React.createElement(React.Fragment, null,
        React.createElement(PreviewContent, null,
            React.createElement(ErrorBoundary, null, fileInfoFromPropsOrRpc && fileMetadataFromPropsOrRpc ? (React.createElement(PreviewRenderer, { ...props, fileInfo: fileInfoFromPropsOrRpc, fileMetadata: fileMetadataFromPropsOrRpc, fileConfig: fileConfig, activeOverlayEditor: activeOverlayEditor })) : previewMetadata.error ? (React.createElement(PreviewRenderer, { ...props, error: previewMetadata.error, fileConfig: fileConfig, activeOverlayEditor: activeOverlayEditor })) : (React.createElement(LoadingIndicator, null)))),
        React.createElement(PreviewKeyboardBindings, { enableFullscreen: featureConfig.enable_fullscreen, isDeleteEnabled: !activeOverlayEditor, isFileFlippingEnabled: fileMetadataFromPropsOrRpc != null &&
                !!fileMetadataFromPropsOrRpc.file_collection_context &&
                !activeOverlayEditor, fileIndex: ((_e = fileMetadataFromPropsOrRpc === null || fileMetadataFromPropsOrRpc === void 0 ? void 0 : fileMetadataFromPropsOrRpc.file_collection_context) === null || _e === void 0 ? void 0 : _e.file_index) || 0, collectionFileCount: ((_f = fileMetadataFromPropsOrRpc === null || fileMetadataFromPropsOrRpc === void 0 ? void 0 : fileMetadataFromPropsOrRpc.file_collection_context) === null || _f === void 0 ? void 0 : _f.parent_files_count) || 0 })));
};
PreviewWithSize.displayName = "PreviewWithSize"
const USER_ENGAGE_LOG_DELAY = 3 * 1000;
/**
 * Resolve the preview content renderer for a given file.
 * Returns a function that can render a filetype-specific preview given
 * filetype-specific props.
 * Note that cloud docs are classed as SSR docs for the purposes of this renderer.
 * Unsupported cloud doc types (e.g. gsheets) are handled during the
 * `fetchRivieraData` portion of the bootstrapping phase.
 */
const PreviewRenderer = ({ callbacks, ...props }) => {
    var _a;
    const { fileInfo, fileMetadata, textHighlighters, ssrDocJumpToPageIndex } = props;
    const { featureConfig } = useFileViewerContext();
    const previewMetadata = useCurrentPreviewMetadata();
    const previewType = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.previewType;
    const previewKey = useCurrentPreviewKey();
    const previewSessionId = useInitializePreviewSession(previewKey, fileInfo, fileMetadata, callbacks === null || callbacks === void 0 ? void 0 : callbacks.onPreviewSessionInitialized, previewMetadata);
    useSupportConfirmed(previewSessionId, !!previewType && previewType !== PreviewType.Loading && previewType !== PreviewType.Error);
    useMarkTimingEventOnMount(PreviewsTimingEvents.PREVIEW_RENDERER_MOUNT);
    useEndSession(previewSessionId);
    useTimingLogging(previewType);
    usePurgeQueuedEvents();
    const navigateToPageCallback = useNavigateToPageCallback(UserActionContext.MobileAppToWebPreview);
    if (ssrDocJumpToPageIndex) {
        navigateToPageCallback(ssrDocJumpToPageIndex);
    }
    const logPreviewEvent = useLogPreviewEvent();
    const logFileViewerEngaged3Seconds = useDelayCallback(() => {
        logPreviewEvent(EventType.UserEngaged3SecondsAfterRenderSucceeded);
    }, USER_ENGAGE_LOG_DELAY, 
    // We will cancel logging the event when user flips to another file
    [previewSessionId]);
    const onRenderSucceeded = useStabilizedCallback(() => {
        var _a;
        logFileViewerEngaged3Seconds();
        (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onRenderSucceeded) === null || _a === void 0 ? void 0 : _a.call(callbacks);
    });
    const setLocalError = useSetLocalErrorCallback(previewKey);
    useMaybeSetPasswordProtectedArchiveError();
    const textHighlightersInstances = React.useMemo(() => {
        return (textHighlighters &&
            Object.values(textHighlighters).filter((textHighlighter) => !!textHighlighter));
    }, [textHighlighters]);
    const activeTextHighlighterId = useActiveTextHighlighterId();
    const activeTextHighlighter = activeTextHighlighterId
        ? textHighlighters === null || textHighlighters === void 0 ? void 0 : textHighlighters[activeTextHighlighterId]
        : undefined;
    const cloudDocWillPreviewAsSsrDoc = getWillCloudDocPreviewAsSsrDoc(featureConfig, previewMetadata);
    if (!previewSessionId || !previewKey || !previewType) {
        if (previewType === PreviewType.Error) {
            return (React.createElement(AsyncPreviewError, { error: (previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.error) || new FVError(FVErrorCode.Unknown, ErrorLifecycle.Render), ...props, 
                // Unfortunately we can't log in this case. The main way to hit it is the rare case of when file ID
                // is unknown, and we don't want to create a path for logging without file ID.
                previewSessionId: "", previewKey: previewKey, onRenderFailed: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onRenderFailed }));
        }
        else {
            return null;
        }
    }
    const additionalProps = {
        previewSessionId,
        onRenderSucceeded,
        previewKey,
        textHighlightersInstances,
        activeTextHighlighter,
        featureConfig,
        setLocalError,
    };
    switch (previewType) {
        case PreviewType.Image:
            return React.createElement(AsyncPreviewImage, { ...props, ...additionalProps, key: previewKey.serialized });
        case PreviewType.SsrDoc:
        case PreviewType.CloudDoc:
            if (!cloudDocWillPreviewAsSsrDoc) {
                return React.createElement(AsyncPreviewCloudDoc, { ...props, ...additionalProps, key: previewKey.serialized });
            }
            else if ((_a = fileMetadata === null || fileMetadata === void 0 ? void 0 : fileMetadata.file_name) === null || _a === void 0 ? void 0 : _a.endsWith('.gsheet')) {
                return (React.createElement(AsyncPreviewHTML, { ...props, ...additionalProps, isRawHtml: true, key: previewKey.serialized }));
            }
            return React.createElement(AsyncPreviewDoc, { ...props, ...additionalProps, key: previewKey.serialized });
        case PreviewType.Video:
        case PreviewType.Audio:
            return (React.createElement(AsyncPreviewAudioVideo, { ...props, ...additionalProps, isAudio: previewType === PreviewType.Audio, key: previewKey.serialized }));
        case PreviewType.Excel:
        case PreviewType.RawHTML:
            return (React.createElement(AsyncPreviewHTML, { ...props, ...additionalProps, isRawHtml: previewType === PreviewType.RawHTML, key: previewKey.serialized }));
        case PreviewType.Text:
            return React.createElement(AsyncPreviewText, { ...props, ...additionalProps, key: previewKey.serialized });
        case PreviewType.Archive:
            return React.createElement(AsyncPreviewArchive, { ...props, ...additionalProps, key: previewKey.serialized });
        case PreviewType.Linkfile:
            return React.createElement(AsyncPreviewLinkfile, { ...props, ...additionalProps, key: previewKey.serialized });
        case PreviewType.Error:
            return (React.createElement(AsyncPreviewError, { error: (previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.error) || new FVError(FVErrorCode.Unknown, ErrorLifecycle.Render), ...props, ...additionalProps, onRenderFailed: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onRenderFailed }));
        case PreviewType.Loading:
            return React.createElement(LoadingIndicator, null);
    }
    return (React.createElement(AsyncPreviewError, { error: new FVError(FVErrorCode.UnsupportedPreviewType, ErrorLifecycle.Render), ...props, ...additionalProps, onRenderFailed: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onRenderFailed }));
};
PreviewRenderer.displayName = "PreviewRenderer"
PreviewRenderer.displayName = 'PreviewRenderer';
//# sourceMappingURL=preview.preenvesnextjs.map
